/**
 * @fileoverview Privacy Policy page for Compass Ledger.
 * Accessible at /privacy for unauthenticated and authenticated users.
 */

import styles from './LegalPage.module.css';

/**
 * PrivacyPolicyPage Component
 *
 * Privacy policy for Compass Ledger.
 *
 * @param {Object} props
 * @param {Function} props.onBack - Navigate back to landing page
 */
function PrivacyPolicyPage({ onBack }) {
    return (
        <div className={styles.container}>
            <div className={styles.content}>
                <button onClick={onBack} className={styles.backButton}>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M19 12H5M12 19l-7-7 7-7" />
                    </svg>
                    Back to Home
                </button>

                <h1 className={styles.title}>Privacy Policy</h1>
                <p className={styles.lastUpdated}>Effective date: March 14, 2025</p>

                <div className={styles.section}>
                    <p className={styles.text}>
                        Compass Ledger ("we," "us," or "our") operates the Compass Ledger web application
                        (the "Service"). This Privacy Policy explains what information we collect, how we
                        use it, and your rights regarding that information.
                    </p>
                    <p className={styles.text}>
                        By using the Service, you agree to the collection and use of information as
                        described in this policy.
                    </p>
                </div>

                <div className={styles.section}>
                    <h2 className={styles.sectionTitle}>1. Information We Collect</h2>

                    <h3 className={styles.subsectionTitle}>Account Information</h3>
                    <p className={styles.text}>
                        When you sign in using Google, we receive your name and email address from
                        Google via Auth0, our authentication provider. We store this information in
                        our database to identify your account and display your name within the app.
                    </p>

                    <h3 className={styles.subsectionTitle}>Budget and Financial Data</h3>
                    <p className={styles.text}>
                        The core purpose of the Service is budget tracking. We store the data you
                        enter, including:
                    </p>
                    <ul className={styles.list}>
                        <li>Budget names and configuration</li>
                        <li>Transaction amounts, dates, descriptions, and categories</li>
                        <li>Category names and spending targets</li>
                        <li>Any other information you choose to enter into the app</li>
                    </ul>
                    <p className={styles.text}>
                        This data is stored on your behalf and used solely to provide the Service.
                        We do not analyze, sell, or share your financial data with any third parties.
                    </p>

                    <h3 className={styles.subsectionTitle}>Usage Information</h3>
                    <p className={styles.text}>
                        We record your last login timestamp. We do not currently use analytics
                        services, advertising trackers, or session recording tools.
                    </p>
                </div>

                <div className={styles.section}>
                    <h2 className={styles.sectionTitle}>2. How We Use Your Information</h2>
                    <p className={styles.text}>We use the information we collect to:</p>
                    <ul className={styles.list}>
                        <li>Authenticate you and maintain your account</li>
                        <li>Store and display your budget and transaction data</li>
                        <li>Enable collaboration with other users you invite to a shared budget</li>
                        <li>Respond to requests or questions you send us</li>
                    </ul>
                    <p className={styles.text}>
                        We do not sell your personal information. We do not use your financial data
                        for advertising or profiling.
                    </p>
                </div>

                <div className={styles.section}>
                    <h2 className={styles.sectionTitle}>3. Sharing Your Information</h2>
                    <p className={styles.text}>
                        We do not sell or rent your personal information. We share data only with
                        the following service providers, and only to the extent necessary to operate
                        the Service:
                    </p>
                    <ul className={styles.list}>
                        <li>
                            <strong>Auth0</strong> — Authentication provider. Handles sign-in and
                            securely stores authentication credentials. Auth0 acts as a broker for
                            Google Sign-In.{' '}
                            <a href="https://auth0.com/privacy" target="_blank" rel="noopener noreferrer" className={styles.link}>
                                Auth0 Privacy Policy
                            </a>
                        </li>
                        <li>
                            <strong>Google</strong> — Identity provider for "Sign in with Google."
                            We receive your name and email from Google when you authenticate.{' '}
                            <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" className={styles.link}>
                                Google Privacy Policy
                            </a>
                        </li>
                        <li>
                            <strong>Railway</strong> — Cloud hosting provider for our backend
                            server and database.{' '}
                            <a href="https://railway.app/legal/privacy" target="_blank" rel="noopener noreferrer" className={styles.link}>
                                Railway Privacy Policy
                            </a>
                        </li>
                        <li>
                            <strong>Vercel</strong> — Cloud hosting provider for our frontend
                            application.{' '}
                            <a href="https://vercel.com/legal/privacy-policy" target="_blank" rel="noopener noreferrer" className={styles.link}>
                                Vercel Privacy Policy
                            </a>
                        </li>
                    </ul>
                    <p className={styles.text}>
                        We may disclose your information if required by law or in response to a
                        valid legal process.
                    </p>
                </div>

                <div className={styles.section}>
                    <h2 className={styles.sectionTitle}>4. Data Retention</h2>
                    <p className={styles.text}>
                        We retain your account and budget data for as long as you have an active
                        account with us. If you would like your data deleted, please contact us
                        at the email below and we will remove your account and associated data
                        within 30 days.
                    </p>
                </div>

                <div className={styles.section}>
                    <h2 className={styles.sectionTitle}>5. Data Security</h2>
                    <p className={styles.text}>
                        All data is transmitted over HTTPS. Your database records are hosted on
                        Railway's infrastructure with access controls in place. Authentication is
                        handled entirely by Auth0 — we never store or see your Google password.
                    </p>
                    <p className={styles.text}>
                        While we take reasonable measures to protect your data, no method of
                        internet transmission or electronic storage is 100% secure. We cannot
                        guarantee absolute security.
                    </p>
                </div>

                <div className={styles.section}>
                    <h2 className={styles.sectionTitle}>6. Children's Privacy</h2>
                    <p className={styles.text}>
                        The Service is not intended for use by anyone under the age of 13. We do
                        not knowingly collect personal information from children under 13. If you
                        believe a child has provided us with personal information, please contact
                        us and we will delete it.
                    </p>
                </div>

                <div className={styles.section}>
                    <h2 className={styles.sectionTitle}>7. Changes to This Policy</h2>
                    <p className={styles.text}>
                        We may update this Privacy Policy from time to time. We will update the
                        effective date at the top of this page when we do. Continued use of the
                        Service after changes are posted constitutes your acceptance of the updated
                        policy.
                    </p>
                </div>

                <div className={styles.section}>
                    <h2 className={styles.sectionTitle}>8. Contact Us</h2>
                    <p className={styles.text}>
                        If you have questions about this Privacy Policy, would like to request
                        deletion of your data, or have any other privacy-related concerns, please
                        contact us at:
                    </p>
                    <p className={styles.contactEmail}>
                        compassledger.app@gmail.com
                    </p>
                </div>
            </div>
        </div>
    );
}

export default PrivacyPolicyPage;
